<script setup lang="ts">
/**
 * Checkbox Source - anatomie Figma : case + label (+ description).
 * Pas de variante card.
 */
type Size = 'small' | 'medium'

type Props = {
  modelValue: boolean
  label: string
  description?: string
  icon?: string
  disabled?: boolean
  size?: Size
}

const props = withDefaults(defineProps<Props>(), {
  description: '',
  icon: undefined,
  disabled: false,
  size: 'medium',
})

const emit = defineEmits<{
  (event: 'update:modelValue', value: boolean): void
}>()

function toggle(): void {
  if (props.disabled) {
    return
  }
  emit('update:modelValue', !props.modelValue)
}

function onKeydown(keyboardEvent: KeyboardEvent): void {
  if (keyboardEvent.key === ' ' || keyboardEvent.key === 'Enter') {
    keyboardEvent.preventDefault()
    toggle()
  }
}
</script>

<template>
  <button
    type="button"
    role="checkbox"
    class="source-checkbox"
    :class="[size, { 'is-checked': modelValue, 'is-disabled': disabled }]"
    :aria-checked="modelValue"
    :aria-disabled="disabled || undefined"
    :disabled="disabled"
    @click="toggle"
    @keydown="onKeydown"
  >
    <span class="source-checkbox__box" aria-hidden="true">
      <span class="material-symbols-rounded source-checkbox__check">check</span>
    </span>

    <span v-if="icon" class="material-symbols-rounded source-checkbox__icon" aria-hidden="true">
      {{ icon }}
    </span>

    <span class="source-checkbox__texts">
      <span class="source-checkbox__label">{{ label }}</span>
      <span v-if="description" class="source-checkbox__description">{{ description }}</span>
    </span>
  </button>
</template>

<style scoped lang="scss">
@use '~/assets/scss/partials/elpdl/colors' as c;
@use '~/assets/scss/partials/elpdl/spacing' as *;

.source-checkbox {
  display: inline-flex;
  align-items: center;
  gap: $spacing-2xs;
  width: auto;
  max-width: 100%;
  margin: 0;
  padding: 0;
  border: none;
  border-radius: 0;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: inherit;

  &:focus-visible {
    outline: 2px solid c.$enedis-primary-400;
    outline-offset: $spacing-3xs;
  }

  &.is-disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  &.small {
    gap: $spacing-2xs;

    .source-checkbox__label {
      font-size: 0.875rem;
    }

    .source-checkbox__box {
      width: 16px;
      height: 16px;
      border-width: 1.5px;
    }

    .source-checkbox__check {
      font-size: 14px;
    }
  }
}

.source-checkbox__box {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border: 1.5px solid c.$enedis-neutral-300;
  border-radius: 4px;
  background: c.$enedis-neutral-10;
  // box-shadow (pas de pseudo-élément à z-index négatif) pour le halo hover,
  // afin qu'il reste visible au-dessus du fond des ancêtres.
  box-shadow: 0 0 0 0 transparent;
  transition: border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;

  .source-checkbox:hover:not(.is-disabled) & {
    border-color: c.$enedis-primary-500;
    box-shadow: 0 0 0 6px c.$enedis-primary-200;
  }

  .source-checkbox.is-checked & {
    border-color: c.$enedis-primary-500;
    background: c.$enedis-primary-500;
  }

  .source-checkbox.is-checked:hover:not(.is-disabled) & {
    box-shadow: 0 0 0 6px c.$enedis-primary-200;
  }
}

.source-checkbox__check {
  font-size: 16px;
  line-height: 1;
  color: #fff;
  opacity: 0;
  transform: scale(0.6);
  transition: opacity 0.15s ease, transform 0.15s ease;

  .source-checkbox.is-checked & {
    opacity: 1;
    transform: scale(1);
  }
}

.source-checkbox__icon {
  font-size: 20px;
  line-height: 1;
  color: c.$enedis-primary-500;
  flex-shrink: 0;

  .source-checkbox.is-disabled & {
    color: c.$enedis-neutral-200;
  }
}

.source-checkbox__texts {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.source-checkbox__label {
  font-family: Public Sans, sans-serif;
  font-size: 0.875rem;
  font-weight: 400;
  color: c.$enedis-neutral-900;
  line-height: 1.25rem;
}

.source-checkbox__description {
  font-size: 0.75rem;
  color: c.$enedis-neutral-300;
  line-height: 1.3;
}
</style>
